import { WithDisposable } from '@blocksuite/block-std';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  MindmapElementModel,
  type MindmapNode,
} from '../../../../surface-block/element-model/mindmap.js';
import { getDepthScale } from '../../../../surface-block/element-model/utils/mindmap/style.js';
import type { EdgelessRootBlockComponent } from '../../edgeless-root-block.js';

type TimestampTagInfo = {
  id: string;
  nodeId: string;
  mindmap: MindmapElementModel;
  node: MindmapNode;
  timestamp: string;
  x: number;
  y: number;
  width: number;
  height: number;
  depth: number;
};

const TAG_SIZE = 16;
void TAG_SIZE;

@customElement('edgeless-mindmap-node-timestamps')
export class EdgelessMindmapNodeTimestamps extends WithDisposable(LitElement) {
  static override styles = css`
    .mindmap-timestamps-container {
      position: absolute;
      top: 0;
      left: 0;
      pointer-events: none;
      z-index: 1;
    }

    .timestamp-tag-wrapper {
      width: 16px;
      height: 16px;
      position: absolute;
      z-index: 1;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .timestamp-tag {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 1px 4px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 400;
      font-family: var(--affine-font-family);
      white-space: nowrap;
      color: var(--affine-text-primary-color);
    }

    .timestamp-tag svg {
      width: 12px;
      height: 12px;
      flex-shrink: 0;
    }
  `;

  @property({ attribute: false })
  accessor edgeless!: EdgelessRootBlockComponent;

  @state()
  private accessor _tags: TimestampTagInfo[] = [];

  override connectedCallback() {
    super.connectedCallback();

    this._updateTags();

    this._disposables.add(
      this.edgeless.service.viewport.viewportUpdated.on(() => {
        this.requestUpdate();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementUpdated.on(() => {
        this._updateTags();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementAdded.on(() => {
        this._updateTags();
      })
    );

    this._disposables.add(
      this.edgeless.surfaceBlockModel.elementRemoved.on(() => {
        this._updateTags();
      })
    );
  }

  private _updateTags() {
    const tags: TimestampTagInfo[] = [];
    const elements =
      (this.edgeless.surfaceBlockModel as unknown as { getElementsByType(type: string): unknown[] }).getElementsByType('mindmap');

    for (const element of elements) {
      if (!(element instanceof MindmapElementModel)) continue;

      const mindmap = element;
      const getMeta =
        typeof (mindmap as any).getNodeMetadata === 'function'
          ? (id: string) => (mindmap as any).getNodeMetadata(id)
          : null;

      mindmap.traverse(
        (node: MindmapNode) => {
          const detail = node.detail as { timestamp?: string | null };
          const meta = getMeta ? getMeta(node.id) : detail;
          const timestamp = meta?.timestamp ?? null;

          if (!timestamp || timestamp === '0:00' || timestamp === '00:00')
            return;

          const bound = node.element.elementBound;

          const path = mindmap.getPath(node.id);
          const depth = path.length - 1;

          tags.push({
            id: `${mindmap.id}-${node.id}`,
            nodeId: node.id,
            mindmap,
            node,
            timestamp,
            x: bound.x,
            y: bound.y,
            width: bound.w,
            height: bound.h,
            depth,
          });
        },
        { stopOnCollapse: true }
      );
    }

    this._tags = tags;
  }

  override render() {
    if (this._tags.length === 0) {
      return nothing;
    }

    const { viewport } = this.edgeless.service;
    const { zoom } = viewport;

    return html`
      <div class="mindmap-timestamps-container">
        ${repeat(
          this._tags,
          info => info.id,
          info => {
            const [nodeViewX, nodeViewY] = viewport.toViewCoord(info.x, info.y);
            const nodeViewWidth = info.width * zoom;
            const nodeViewHeight = info.height * zoom;

            const depthScale = getDepthScale(info.depth);
            const fontSize = Math.round(11 * depthScale);
            const iconSize = Math.round(10 * depthScale);
            const padding = Math.round(4 * depthScale);

            const tagX = nodeViewX + nodeViewWidth * 0.75;
            const tagY = nodeViewY + nodeViewHeight + padding * zoom;

            const transform = `translate(${tagX - TAG_SIZE / 2}px, ${tagY}px) scale(${zoom})`;

            return html`
              <div
                class="timestamp-tag-wrapper"
                style=${styleMap({
                  transform,
                  transformOrigin: 'left top',
                })}
              >
                <div
                  class="timestamp-tag"
                  style=${styleMap({ fontSize: `${fontSize}px` })}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    style=${styleMap({
                      width: `${iconSize}px`,
                      height: `${iconSize}px`,
                    })}
                  >
                    <path
                      fill="currentColor"
                      d="M12,1.25 C17.937,1.25 22.75,6.063 22.75,12 C22.75,17.937 17.937,22.75 12,22.75 C6.063,22.75 1.25,17.937 1.25,12 C1.25,6.063 6.063,1.25 12,1.25 Z M2.75,12 C2.75,17.109 6.891,21.25 12,21.25 C17.109,21.25 21.25,17.109 21.25,12 C21.25,6.891 17.109,2.75 12,2.75 C6.891,2.75 2.75,6.891 2.75,12 Z M12.75,8 L12.75,11.689 L14.53,13.47 C14.823,13.763 14.823,14.237 14.53,14.53 C14.237,14.823 13.763,14.823 13.47,14.53 L11.47,12.53 C11.329,12.39 11.25,12.199 11.25,12 L11.25,8 C11.25,7.586 11.586,7.25 12,7.25 C12.414,7.25 12.75,7.586 12.75,8 Z"
                    />
                  </svg>
                  ${info.timestamp}
                </div>
              </div>
            `;
          }
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-mindmap-node-timestamps': EdgelessMindmapNodeTimestamps;
  }
}
